import React, { useEffect, useState, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import noop from "lodash/noop"
import throttle from "lodash/throttle"
import { BASE_ELEMENT_ID } from "src/configuration"
import hocInterface from "src/helpers/hocInterface"
import {
  LAYER_HEIGHT,
  GROUPS_HIDING_THROTTLE_DELAY,
  GROUPS_HIDING_ADDITIONAL_PAGES,
} from "src/containers/Calendar/constants"

class Range {
  static intersects = (range1, range2) => {
    const intersects = (x1, x2, y1, y2) => x2 > y1 && y2 > x1
    return intersects(
      range1.getStart(),
      range1.getEnd(),
      range2.getStart(),
      range2.getEnd(),
    )
  }

  constructor(x, y) {
    this.x = x
    this.y = y
  }

  getStart = () => this.x

  getEnd = () => this.y
}

const scrollElementEmpty = {
  scrollTop: 0,
  addEventListener: noop,
  removeEventListener: noop,
}

const useWindowScroll = scrollElement => {
  const [scroll, setScroll] = useState(scrollElement.scrollTop)

  const f = useCallback(
    throttle(() => {
      setScroll(scrollElement.scrollTop)
    }, GROUPS_HIDING_THROTTLE_DELAY),
    [scrollElement],
  )

  useEffect(() => {
    scrollElement.addEventListener(`scroll`, f)
    return () => {
      scrollElement.removeEventListener(`scroll`, f)
    }
  }, [scrollElement])

  return scroll
}

const useWindowHeight = () => {
  const [height, setHeight] = useState(window.innerHeight)

  const f = useCallback(
    throttle(() => {
      setHeight(window.innerHeight)
    }, GROUPS_HIDING_THROTTLE_DELAY),
    [],
  )

  useEffect(() => {
    window.addEventListener(`resize`, f)
    return () => {
      window.removeEventListener(`resize`, f)
    }
  }, [])

  return height
}

const truncInvisibleRows = (rows, visibleRange) => {
  const newRows = []
  let accumulatedHeight = 0
  let realStart = 0
  let isRealStartSet = false
  let realEnd = 0
  let isRealEndSet = false

  rows.forEach(row => {
    const rowRange = new Range(
      accumulatedHeight,
      accumulatedHeight + row.layers * LAYER_HEIGHT,
    )
    const isRowInRange = Range.intersects(rowRange, visibleRange)
    if (isRowInRange) {
      newRows.push(row)
      if (!isRealStartSet) {
        realStart = rowRange.getStart()
        isRealStartSet = true
      }
    } else if (isRealStartSet && !isRealEndSet) {
      realEnd = rowRange.getEnd()
      isRealEndSet = true
    }
    accumulatedHeight = rowRange.getEnd()
  })

  if (!isRealEndSet) {
    realEnd = accumulatedHeight
    isRealEndSet = true
  }

  return {
    rows: newRows,
    paddingTop: realStart,
    paddingBottom: accumulatedHeight - realEnd,
    height: accumulatedHeight,
  }
}

const useVisibleRange = (currentScroll, currentHeight) =>
  useMemo(
    () =>
      new Range(
        currentScroll - GROUPS_HIDING_ADDITIONAL_PAGES * currentHeight,
        currentScroll + (1 + GROUPS_HIDING_ADDITIONAL_PAGES) * currentHeight,
      ),
    [currentScroll, currentHeight],
  )

/**
 * Обрезает "невидимые" строки и создают соответствующие вертикальные отступы вместо них для календаря
 */
export default hocInterface(
  {
    consumeProps: {
      groupsCache: PropTypes.arrayOf(
        PropTypes.shape({
          layers: PropTypes.number.isRequired,
        }),
      ).isRequired,
      onReachEnd: PropTypes.func.isRequired,
    },
    exposeProps: [
      `groupsCache`,
      `replacementHeightTop`,
      `replacementHeightBottom`,
    ],
  },
  BaseComponent => props => {
    const { groupsCache, onReachEnd } = props

    const [scrollElement, setScrollElement] = useState(scrollElementEmpty)

    useEffect(() => {
      if (scrollElement === scrollElementEmpty) {
        const element = document.getElementById(BASE_ELEMENT_ID)
        if (element) {
          setScrollElement(element)
        }
      }
    })

    const currentHeight = useWindowHeight()
    const currentScroll = useWindowScroll(scrollElement)

    const visibleRange = useVisibleRange(currentScroll, currentHeight)

    const { rows: newRows, paddingTop, paddingBottom, height } = useMemo(
      () => truncInvisibleRows(groupsCache, visibleRange),
      [groupsCache, Math.trunc(visibleRange.getStart() / currentHeight)],
    )

    useEffect(() => {
      if (visibleRange.getEnd() >= height) {
        onReachEnd()
      }
    }, [height, visibleRange])

    return (
      <BaseComponent
        {...props}
        replacementHeightTop={paddingTop}
        replacementHeightBottom={paddingBottom}
        groupsCache={newRows}
      />
    )
  },
)
